import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { PARTNER_REVENUE_PRESETS } from "../utils/partnerRevenuePeriodPresets";
import { usePartnerRevenuePeriod } from "../hooks/usePartnerRevenuePeriod";

/**
 * Task 0038 (owner, 2026-09-30): the one period selector for every partner
 * orders/earnings page — preset buttons + a "Custom" range picker (future
 * dates allowed), "By service date" label, state in the URL. Call
 * `usePartnerRevenuePeriod(paramPrefix)` in the hosting page with the same
 * `paramPrefix` to read the resulting range for queries — this component
 * and the page's own hook call share state purely through the URL, no
 * prop drilling needed.
 */
export const PartnerPeriodSelector: React.FC<{ paramPrefix?: string }> = ({
  paramPrefix = "period",
}) => {
  const { t } = useTranslation();
  const { periodId, range, setPreset, setCustomRange } =
    usePartnerRevenuePeriod(paramPrefix);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap items-center gap-1">
        {PARTNER_REVENUE_PRESETS.map((preset) => (
          <Button
            key={preset}
            type="button"
            variant={periodId === preset ? "default" : "outline"}
            size="sm"
            onClick={() => setPreset(preset)}
          >
            {t(`partners.detail.periodPresets.${preset}`)}
          </Button>
        ))}
      </div>
      <DateRangePicker
        dateRange={
          periodId === "custom"
            ? { from: range.from, to: range.to }
            : { from: undefined, to: undefined }
        }
        onDateRangeChange={(next) => {
          if (next.from && next.to) {
            setCustomRange({ from: next.from, to: next.to });
          }
        }}
        triggerVariant="new_outline"
      />
      <span className="text-muted-foreground text-xs">
        {t("partners.detail.serviceDatePeriod")}
      </span>
    </div>
  );
};
